import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { aiApi, opportunityApi, pipelineApi, userApi, complianceMatrixApi, proposalReviewApi } from '../../services/api';
import PricingModal from '../../components/proposal/PricingModal';
import GovernmentFormsModal from '../../components/proposal/GovernmentFormsModal';
import toast from 'react-hot-toast';
import {
  DocumentTextIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
  ClipboardDocumentIcon,
  ArrowDownTrayIcon,
  TableCellsIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from 'components/icons';
import Modal from '../../components/ui/Modal';
import { SkeletonRows, SkeletonBlock, SkeletonLine } from '../../components/ui/Skeleton';
import ErrorState from '../../components/ui/ErrorState';
import ProposalChecklist from '../../components/proposal/ProposalChecklist';
import ClassificationGate from '../../components/proposal/ClassificationGate';
import BidSchedulePanel from '../../components/proposal/BidSchedulePanel';
import PageHeader from '../../components/ui/PageHeader';

interface GeneratedSection {
  sectionType: string;
  title: string;
  content: string;
  wordCount: number;
  estimatedPages: number;
  complianceNotes: string[];
  complianceScore?: number | null;
  complianceGaps?: string[];
  complianceRecommendations?: string[];
  contractType?: string;
  evaluationMethod?: string;
  /** Server-side findings about this section. Carries placeholderSources when
   *  the material it was written from was marked as test or stand-in data. */
  metadata?: any;
}

interface RequiredSection {
  name: string;
  description: string;
  pageLimit?: number;
  required: boolean;
  matchedSection?: string;
}


/**
 * Render the light markdown the writer emits, so the preview matches the export.
 *
 * The DOCX exporter already turns **bold** into real bold runs — a generated
 * proposal contains no asterisks. The preview was printing the source verbatim,
 * so the same section looked broken on screen and correct in the file, and the
 * obvious conclusion was that the proposal was full of stray characters.
 *
 * Deliberately narrow: bold, and headings written with leading hashes. Anything
 * else passes through untouched, because inventing formatting the exporter does
 * not apply would put the two out of step again in the other direction.
 */
const renderProposalText = (content: string): React.ReactNode =>
  content.split('\n').map((line, i) => {
    const heading = line.match(/^(#{1,3})\s+(.*)$/);
    const body = heading ? heading[2] : line;

    const parts = body.split(/(\*\*[^*]+\*\*)/g).map((part, j) =>
      part.startsWith('**') && part.endsWith('**') ? (
        <strong key={j}>{part.slice(2, -2)}</strong>
      ) : (
        <React.Fragment key={j}>{part}</React.Fragment>
      ),
    );

    if (heading) {
      const level = heading[1].length;
      return (
        <div
          key={i}
          className={
            level === 1
              ? 'mt-4 mb-2 text-lg font-bold'
              : level === 2
              ? 'mt-3 mb-1.5 font-bold'
              : 'mt-2 mb-1 font-semibold'
          }
        >
          {parts}
        </div>
      );
    }
    return <div key={i}>{parts.length ? parts : '\u00a0'}</div>;
  });

const ProposalWriter: React.FC = () => {
  const [searchParams] = useSearchParams();
  const opportunityId = searchParams.get('opportunityId');
  const navigate = useNavigate();

  // Pipeline opportunities for selector
  const [pipelineOpps, setPipelineOpps] = useState<any[]>([]);

  // Opportunity context
  const [opportunity, setOpportunity] = useState<any>(null);
  const [loadingOpp, setLoadingOpp] = useState(false);
  const [oppError, setOppError] = useState(false);
  const [requirementsError, setRequirementsError] = useState(false);

  // Solicitation requirements
  const [requirements, setRequirements] = useState<any>(null);
  const [requirementsMessage, setRequirementsMessage] = useState<string>('');
  const [loadingRequirements, setLoadingRequirements] = useState(false);
  const [contractType, setContractType] = useState<string>('unknown');
  const [evaluationMethod, setEvaluationMethod] = useState<string>('unknown');
  const [impliedSections, setImpliedSections] = useState<string[]>([]);

  // Generation state
  const [generatingSection, setGeneratingSection] = useState<string | null>(null);
  const [tone, setTone] = useState('professional');
  const [proposalId, setProposalId] = useState<string | null>(null);

  // Generated sections
  const [sections, setSections] = useState<GeneratedSection[]>([]);
  const [activeSection, setActiveSection] = useState<number | null>(null);

  // Export state
  const [exporting, setExporting] = useState(false);
  const [showExportChecklist, setShowExportChecklist] = useState(false);
  // The NAICS codes on the company profile, used to decide how loudly to warn
  // about the SAM registration on export.
  const [profileNaics, setProfileNaics] = useState<string[]>([]);
  const [gaps, setGaps] = useState<any>(null);
  // Reset every time the dialog opens. Each export is a potential submission,
  // so the acknowledgement is per-export rather than per-account — ticking it
  // once should not silence it for every future bid.
  const [samAcknowledged, setSamAcknowledged] = useState(false);
  const [exportFormat, setExportFormat] = useState('docx-single');

  // Company-data readiness (soft warning — not a hard block)
  const [readiness, setReadiness] = useState<{ ready: boolean; missing: string[] } | null>(null);
  const [readinessDismissed, setReadinessDismissed] = useState(false);

  // Bid readiness checklist
  const [checklistItems, setChecklistItems] = useState<any[]>([]);
  const [checklistProgress, setChecklistProgress] = useState<{ total: number; completed: number; allDone: boolean } | null>(null);
  const [checklistReady, setChecklistReady] = useState(false);

  // Classification gate
  const [classification, setClassification] = useState<string | null>(null);
  const [classificationDone, setClassificationDone] = useState(false);

  // Company logo check
  const [hasCompanyLogo, setHasCompanyLogo] = useState<boolean | null>(null);

  // Compliance matrix
  const [matrixItems, setMatrixItems] = useState<any[]>([]);
  const [matrixStats, setMatrixStats] = useState<any>(null);
  const [showMatrix, setShowMatrix] = useState(false);
  const [showBidSchedule, setShowBidSchedule] = useState(false);
  const [loadingMatrix, setLoadingMatrix] = useState(false);

  // Proposal review
  const [reviewing, setReviewing] = useState(false);
  const [reviewResult, setReviewResult] = useState<any>(null);
  const [showReview, setShowReview] = useState(false);

  // Pricing modal
  const [showPricing, setShowPricing] = useState(false);

  // Government forms modal
  const [showForms, setShowForms] = useState(false);

  const loadComplianceMatrix = async () => {
    if (!proposalId) return;
    setLoadingMatrix(true);
    try {
      const res = await complianceMatrixApi.get(proposalId);
      setMatrixItems(res.data?.data?.items || []);
      setMatrixStats(res.data?.data?.stats || null);
    } catch (e: any) {
      toast.error('Failed to load compliance matrix');
    } finally {
      setLoadingMatrix(false);
    }
  };

  const runReview = async () => {
    if (!proposalId) {
      toast.error('Generate at least one section before running a review');
      return;
    }
    setReviewing(true);
    try {
      const res = await proposalReviewApi.review(proposalId);
      setReviewResult(res.data?.data);
      setShowReview(true);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Review failed');
    } finally {
      setReviewing(false);
    }
  };

  // Load pipeline opportunities and check company logo
  useEffect(() => {
    pipelineApi.getOpportunities().then(res => {
      setPipelineOpps(res.data.data?.opportunities || res.data.opportunities || []);
    }).catch(() => {});
    // Check if user has company logo uploaded
    userApi.getMe().then(res => {
      const user = res.data.user;
      // GET /users/me never returned companyLogo — it is a 77KB base64 data
      // URL and that endpoint runs on every app boot — so this was always false
      // and the banner asked for a logo that was already on file.
      setHasCompanyLogo(!!(user as any)?.hasCompanyLogo);
      setProfileNaics(user?.naicsCodes || []);
    }).catch(() => {});
    // Company-data readiness — drives the soft warning banner below.
    userApi.getProposalReadiness().then(res => {
      setReadiness({ ready: !!res.data?.ready, missing: res.data?.missing || [] });
    }).catch(() => {});
  }, []);

  // Load opportunity + existing proposal + requirements + checklist when ID changes
  useEffect(() => {
    if (opportunityId) {
      loadOpportunity();
      loadExistingProposal();
      loadRequirements();
      loadChecklist();
    }
  // Keyed on opportunityId. The four loaders are redefined every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunityId]);

  const loadOpportunity = async () => {
    if (!opportunityId) return;
    setLoadingOpp(true);
    setOppError(false);
    try {
      const response = await opportunityApi.getById(opportunityId);
      setOpportunity(response.data.opportunity);
    } catch (error) {
      // This is the page's anchor record. Failing silently left the writer
      // sitting there with no solicitation context and no explanation.
      console.error('Failed to load opportunity:', error);
      setOppError(true);
    } finally {
      setLoadingOpp(false);
    }
  };

  const loadExistingProposal = async () => {
    if (!opportunityId) return;
    try {
      const response = await aiApi.getProposal(opportunityId);
      const proposal = response.data.data?.proposal;
      if (proposal) {
        setProposalId(proposal.id);
        if (proposal.sections?.length > 0) {
          setSections(proposal.sections.map((s: any) => ({
            sectionType: s.sectionType.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase()),
            title: s.title,
            content: s.content,
            wordCount: s.wordCount || s.content.split(/\s+/).length,
            estimatedPages: Math.ceil((s.wordCount || s.content.split(/\s+/).length) / 400),
            complianceNotes: s.metadata?.complianceNotes || [],
            metadata: s.metadata,
          })));
          setActiveSection(0);
        }
      }
    } catch (error) {
      // A 404 here is the normal "no proposal started yet" path, so this one
      // stays quiet by design — but log it so a real failure is traceable.
      console.debug('No existing proposal loaded:', error);
    }
  };

  const loadRequirements = async (force?: boolean) => {
    if (!opportunityId) return;
    setLoadingRequirements(true);
    setRequirementsError(false);
    try {
      const response = await aiApi.getRequirements(opportunityId, force);
      const data = response.data.data;
      const reqs = data?.requirements;
      setRequirements(reqs);
      setRequirementsMessage(reqs ? '' : (data?.message || ''));
      if (data?.contractType) setContractType(data.contractType);
      if (data?.evaluationMethod) setEvaluationMethod(data.evaluationMethod);
      if (data?.impliedSections) setImpliedSections(data.impliedSections);
    } catch (error) {
      // Requirements drive which sections the writer offers, so a silent
      // failure here looks like "this solicitation has no requirements".
      console.error('Failed to load requirements:', error);
      setRequirementsError(true);
    } finally {
      setLoadingRequirements(false);
    }
  };

  const [checklistGenerationStatus, setChecklistGenerationStatus] = useState<string | null>(null);

  const loadChecklist = async () => {
    if (!opportunityId) return;
    try {
      const response = await aiApi.getChecklist(opportunityId);
      const data = response.data.data;
      setChecklistGenerationStatus(data?.generationStatus || null);
      if (data?.checklist?.length > 0) {
        setChecklistItems(data.checklist);
        setChecklistProgress(data.progress);
        setChecklistReady(data.progress?.allDone || false);
        if (data.proposalId && !proposalId) setProposalId(data.proposalId);
      }
    } catch (error) {
      // The checklist is supplementary — the writer works without it — so this
      // degrades quietly rather than blocking the page.
      console.error('Failed to load checklist:', error);
    }
  };

  const handleOppChange = (newOppId: string) => {
    setSections([]);
    setActiveSection(null);
    setProposalId(null);
    setOpportunity(null);
    setRequirements(null);
    setChecklistItems([]);
    setChecklistProgress(null);
    setChecklistReady(false);
    setClassification(null);
    setClassificationDone(false);
    navigate(`/proposal-writer?opportunityId=${newOppId}`);
  };

  const handleGenerate = async (sectionName: string, sectionDescription: string) => {
    // Warn about missing logo on first generation attempt
    if (hasCompanyLogo === false) {
      toast('Upload your company logo in Company Profile settings for professional cover pages', {
        icon: '⚠️',
        duration: 6000,
      });
    }
    setGeneratingSection(sectionName);
    const toastId = `gen-${sectionName}`;
    const startedAt = Date.now();
    toast.loading(`Generating ${sectionName}… first-run analysis can take a few minutes`, { id: toastId });
    try {
      // Kick off generation as a background job and poll for the result. The
      // first section runs deep solicitation analysis and can take several
      // minutes — far longer than a single HTTP request should be held open.
      const startResp = await aiApi.generateProposalAsync({
        opportunityId: opportunityId || undefined,
        proposalId: proposalId || undefined,
        section: sectionName,
        requirements: sectionDescription || undefined,
        tone,
      });
      const jobId = startResp.data?.jobId;
      if (!jobId) throw new Error('Could not start generation');

      const deadline = startedAt + 15 * 60 * 1000; // give up after 15 min
      let job: any = null;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 4000));
        const statusResp = await aiApi.getGenerationStatus(jobId);
        job = statusResp.data;
        if (job?.status && job.status !== 'running') break;
        const elapsed = Math.floor((Date.now() - startedAt) / 1000);
        const mm = Math.floor(elapsed / 60);
        const ss = elapsed % 60;
        toast.loading(`Generating ${sectionName}… ${mm}m ${String(ss).padStart(2, '0')}s elapsed`, { id: toastId });
      }
      if (!job || job.status !== 'done') {
        throw new Error(job?.error || 'Generation timed out — please try again');
      }

      const gen = job.result.generated;
      const newSection: GeneratedSection = {
        sectionType: sectionName,
        title: gen.title || `${sectionName} Section`,
        content: gen.content,
        wordCount: gen.wordCount || gen.content.split(/\s+/).length,
        estimatedPages: gen.estimatedPages || Math.ceil(gen.content.split(/\s+/).length / 400),
        complianceNotes: gen.complianceNotes || [],
        complianceScore: gen.complianceScore ?? null,
        complianceGaps: gen.complianceGaps || [],
        complianceRecommendations: gen.complianceRecommendations || [],
        contractType: gen.contractType,
        evaluationMethod: gen.evaluationMethod,
        metadata: gen.metadata,
      };

      // Replace if section type already exists, otherwise add
      const existingIdx = sections.findIndex(s => s.sectionType.toLowerCase() === sectionName.toLowerCase());
      if (existingIdx >= 0) {
        setSections(prev => prev.map((s, i) => i === existingIdx ? newSection : s));
        setActiveSection(existingIdx);
      } else {
        setSections(prev => [...prev, newSection]);
        setActiveSection(sections.length);
      }

      if (job.result.proposalId) {
        setProposalId(job.result.proposalId);
      }

      toast.success(`${sectionName} generated`, { id: toastId });
    } catch (error: any) {
      toast.error(error.response?.data?.error || error.message || 'Generation failed', { id: toastId });
    } finally {
      setGeneratingSection(null);
    }
  };

  const handleExportDocx = async () => {
    if (!proposalId) { toast.error('No proposal to export'); return; }
    setExporting(true);
    try {
      const response = await aiApi.exportProposal(proposalId, exportFormat);
      const contentType = String(response.headers['content-type'] ?? 'application/octet-stream');
      const contentDisposition = String(response.headers['content-disposition'] ?? '');
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch?.[1] || `Proposal.${contentType.includes('zip') ? 'zip' : 'docx'}`;
      const blob = new Blob([response.data], { type: contentType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(contentType.includes('zip') ? `Downloaded ${filename} (one file per volume)` : `Downloaded ${filename}`);
    } catch {
      toast.error('Failed to export proposal');
    } finally {
      setExporting(false);
      setShowExportChecklist(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const totalWords = sections.reduce((sum, s) => sum + s.wordCount, 0);
  const totalPages = sections.reduce((sum, s) => sum + s.estimatedPages, 0);

  // Check which required sections are already generated
  const getSectionStatus = (reqName: string): 'generated' | 'missing' => {
    const reqLower = reqName.toLowerCase();
    return sections.some(s => {
      const sLower = s.sectionType.toLowerCase();
      return reqLower.includes(sLower) || sLower.includes(reqLower) || s.title.toLowerCase().includes(reqLower);
    }) ? 'generated' : 'missing';
  };

  // The solicitation's own NAICS — the code the offer is evaluated under.
  // Sections whose source material carried placeholder markers. Computed here
  // so both the section view and the export gate read the same thing.
  const sectionsFromPlaceholders = sections.filter(
    (sec: any) => sec?.metadata?.placeholderSources,
  );

  useEffect(() => {
    if (!showExportChecklist || !proposalId) return;
    aiApi.getProposalGaps(proposalId).then(r => setGaps(r.data)).catch(() => setGaps(null));
  }, [showExportChecklist, proposalId]);

  const samNaicsCode: string | undefined =
    (opportunity as any)?.naicsCodes?.[0] || (opportunity as any)?.naicsCode;

  // A gap in the Clevor profile is a proxy, not proof: SAM is the system of
  // record and we cannot read it. But a user whose own profile does not list
  // the code is unlikely to be registered for it, and that is the case worth
  // stopping. Absent codes entirely counts as a gap — an empty profile cannot
  // vouch for anything.
  const samNaicsGap: boolean = !!samNaicsCode && !profileNaics.includes(samNaicsCode);

  return (
    <div className="space-y-6">
      {/* Company-data readiness — soft warning, does not block generation */}
      {readiness && !readiness.ready && !readinessDismissed && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-start gap-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-amber-800">
            <p className="font-semibold">Add your company details for a stronger first draft</p>
            <p className="mt-1">
              Without{' '}
              {readiness.missing.includes('company_profile') && (
                <Link to="/settings" className="underline font-medium">a completed company profile</Link>
              )}
              {readiness.missing.length === 2 && ' and '}
              {readiness.missing.includes('past_performance') && (
                <Link to="/past-performance" className="underline font-medium">at least one past-performance record</Link>
              )}
              , generated sections will contain <span className="font-mono">[USER: provide …]</span> placeholders you'll have to fill in by hand.
            </p>
          </div>
          <button
            onClick={() => setReadinessDismissed(true)}
            className="text-amber-500 hover:text-amber-700 text-sm font-medium flex-shrink-0"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Header. Keeps a custom body because the subtitle is structured
          opportunity context (title + solicitation number + agency) rather
          than a sentence, but uses PageHeader's actions slot so the title
          treatment and action alignment stay shared. */}
      <PageHeader
        title="AI Proposal Writer"
        subtitle={opportunity ? undefined : 'Generate solicitation-specific proposal sections'}
        actions={
          opportunityId ? (
            <Link
              to={`/opportunities/${opportunityId}`}
              className="btn-ghost"
            >
              <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" /> Back to Opportunity
            </Link>
          ) : undefined
        }
      />
      {opportunity && (
        <div className="-mt-4">
          <p className="text-body-lg text-content-muted truncate max-w-prose">{opportunity.title}</p>
          <div className="flex items-center gap-3 text-caption text-content-muted mt-1">
            {opportunity.solicitationNumber && (
              <span className="font-mono">{opportunity.solicitationNumber}</span>
            )}
            {opportunity.agency && <span>{opportunity.agency}</span>}
          </div>
        </div>
      )}

      {/* Opportunity Selector */}
      {pipelineOpps.length > 0 && (
        <div className="bg-surface shadow rounded-lg p-4">
          <label className="block text-sm font-medium text-content mb-2">Select Opportunity from Pipeline</label>
          <select
            value={opportunityId || ''}
            onChange={(e) => handleOppChange(e.target.value)}
            className="input w-full"
          >
            <option value="">Choose an opportunity...</option>
            {pipelineOpps.map((opp: any) => (
              <option key={opp.id} value={opp.opportunity?.samNoticeId || opp.opportunityId}>
                {opp.opportunity?.title || 'Untitled'} — {opp.opportunity?.agency || 'Unknown'}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Tone selector */}
      {opportunityId && (
        <div className="bg-surface shadow rounded-lg p-4 flex items-center gap-4">
          <label className="text-sm font-medium text-content">Tone:</label>
          <select value={tone} onChange={(e) => setTone(e.target.value)} className="input w-40">
            <option value="professional">Professional</option>
            <option value="aggressive">Aggressive</option>
            <option value="conservative">Conservative</option>
          </select>
        </div>
      )}

      {/* Loading state */}
      {loadingOpp && (
        <div className="card p-5 space-y-3">
          <span className="sr-only" role="status">Loading opportunity</span>
          <SkeletonLine className="w-2/3" />
          <SkeletonLine className="w-1/3" />
        </div>
      )}

      {/* The opportunity is this page's anchor record — surface a failure to
          load it rather than rendering an empty writer. */}
      {opportunityId && !loadingOpp && oppError && (
        <div className="card">
          <ErrorState
            title="Couldn't load this opportunity"
            description="Nothing you've generated is lost. Retry, or go back and reopen it."
            onRetry={loadOpportunity}
            retrying={loadingOpp}
          />
        </div>
      )}

      {/* Classification Gate — must classify before proceeding */}
      {opportunityId && !loadingOpp && !classificationDone && (
        <ClassificationGate
          opportunityId={opportunityId}
          onClassified={(cls) => {
            setClassification(cls);
            setClassificationDone(true);
          }}
          onProfileIncomplete={() => {
            // Profile redirect is handled inside the component
          }}
        />
      )}

      {/* Classification badge with change option */}
      {opportunityId && !loadingOpp && classificationDone && classification && (
        <div className="bg-surface shadow rounded-lg px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-content-muted">Your role for this solicitation:</span>
            <span className="font-medium text-content">
              {classification === 'direct_provider' ? 'Direct Provider' :
               classification === 'direct_service_provider' ? 'Direct Provider' :
               classification === 'generalist_contractor' ? 'Generalist Contractor' : classification}
            </span>
          </div>
          <button
            onClick={async () => {
              if (!opportunityId) return;
              try {
                await pipelineApi.clearClassification(opportunityId);
                setClassificationDone(false);
                setClassification(null);
                setChecklistItems([]);
                setChecklistProgress(null);
                setChecklistReady(false);
              } catch {
                toast.error('Failed to reset classification');
              }
            }}
            className="text-xs text-brand-600 hover:text-brand-700 font-medium"
          >
            Change
          </button>
        </div>
      )}

      {/* Bid Readiness Checklist — gates proposal generation (only after classification) */}
      {opportunityId && !loadingOpp && classificationDone && (
        <ProposalChecklist
          opportunityId={opportunityId}
          checklist={checklistItems}
          progress={checklistProgress}
          proposalId={proposalId}
          initialGenerationStatus={checklistGenerationStatus}
          onChecklistUpdate={(checklist, progress, pId) => {
            setChecklistItems(checklist);
            setChecklistProgress(progress);
            setChecklistReady(progress.allDone);
            if (pId) setProposalId(pId);
          }}
          onAllComplete={() => {
            setChecklistReady(true);
            toast.success('All checklist items complete! You can now generate your proposal sections.');
          }}
        />
      )}

      {/* Company Logo Warning — shown at proposal generation stage */}
      {opportunityId && !loadingOpp && classificationDone && hasCompanyLogo === false && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-800">Company logo not uploaded</p>
            <p className="text-xs text-amber-700 mt-0.5">
              Your company logo is required on proposal cover pages and volume headers for formal government submission.
              Upload it in{' '}
              <Link to="/settings" className="underline font-medium hover:text-amber-900">
                Company Profile settings
              </Link>{' '}
              before exporting your proposal documents.
            </p>
          </div>
        </div>
      )}

      {/* Main content — solicitation-driven (only after classification) */}
      {opportunityId && !loadingOpp && classificationDone && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left — Required Sections from Solicitation */}
          <div className="space-y-4">
            <div className="bg-surface shadow rounded-lg p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-medium text-content">Required Sections</h3>
                <button onClick={() => loadRequirements(true)} disabled={loadingRequirements} className="text-xs text-brand-600 hover:text-brand-700 flex items-center gap-1">
                  <ArrowPathIcon className={`h-3 w-3 ${loadingRequirements ? 'animate-spin' : ''}`} />
                  {loadingRequirements ? 'Analyzing...' : 'Re-analyze'}
                </button>
              </div>

              {/* Contract Type & Evaluation Method Badges */}
              {(contractType !== 'unknown' || evaluationMethod !== 'unknown') && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {contractType !== 'unknown' && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                      {contractType}
                    </span>
                  )}
                  {evaluationMethod !== 'unknown' && (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      evaluationMethod === 'LPTA' ? 'bg-orange-100 text-orange-800' :
                      evaluationMethod === 'BEST_VALUE' ? 'bg-green-100 text-green-800' :
                      'bg-purple-100 text-purple-800'
                    }`}>
                      {evaluationMethod === 'LPTA' ? 'LPTA — Lowest Price Wins' :
                       evaluationMethod === 'BEST_VALUE' ? 'Best Value Tradeoff' :
                       evaluationMethod === 'HIGHEST_TECHNICAL' ? 'Highest Technical' :
                       evaluationMethod.replace(/_/g, ' ')}
                    </span>
                  )}
                </div>
              )}

              {loadingRequirements ? (
                <div className="py-4 space-y-2">
                  <p className="text-caption text-content-muted text-center" role="status">
                    Analyzing solicitation documents…
                  </p>
                  <SkeletonBlock className="h-16" />
                  <SkeletonBlock className="h-16" />
                </div>
              ) : requirementsError ? (
                /* Requirements decide which sections the writer offers, so a
                   silent failure reads as "this solicitation requires nothing". */
                <ErrorState
                  title="Couldn't analyze the solicitation"
                  description="The documents may still be processing. Retrying often works."
                  onRetry={() => loadRequirements(true)}
                  retrying={loadingRequirements}
                  compact
                />
              ) : requirements?.sections ? (
                <div className="space-y-2">
                  {requirements.sections.map((req: RequiredSection, idx: number) => {
                    const status = getSectionStatus(req.name);
                    const isGenerating = generatingSection === req.name;
                    return (
                      <div key={idx} className={`p-3 rounded-lg border ${status === 'generated' ? 'bg-green-50 border-green-200' : 'bg-surface border-border'}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              {status === 'generated'
                                ? <CheckCircleIcon className="h-4 w-4 text-green-500 flex-shrink-0" />
                                : <span className="w-4 h-4 rounded-full border-2 border-border-strong flex-shrink-0" />
                              }
                              <p className="text-sm font-medium text-content truncate">{req.name}</p>
                            </div>
                            <p className="text-xs text-content-muted mt-1 ml-6">{req.description}</p>
                            {req.pageLimit && <p className="text-xs text-content-subtle mt-0.5 ml-6">Max {req.pageLimit} pages</p>}
                          </div>
                          <button
                            onClick={() => handleGenerate(req.name, req.description)}
                            disabled={isGenerating || !!generatingSection || (checklistItems.length > 0 && !checklistReady)}
                            title={checklistItems.length > 0 && !checklistReady ? 'Complete all checklist items first' : undefined}
                            className={`flex-shrink-0 text-xs px-2.5 py-1 rounded-md font-medium transition-colors ${
                              isGenerating
                                ? 'bg-brand-100 text-brand-600'
                                : (checklistItems.length > 0 && !checklistReady)
                                  ? 'bg-surface-sunk text-content-subtle cursor-not-allowed'
                                  : status === 'generated'
                                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                    : 'bg-brand-600 text-white hover:bg-brand-700'
                            } disabled:opacity-50`}
                          >
                            {isGenerating ? (
                              <span className="flex items-center gap-1">
                                <ArrowPathIcon className="h-3 w-3 animate-spin" /> Generating...
                              </span>
                            ) : (checklistItems.length > 0 && !checklistReady) ? '🔒 Locked' : status === 'generated' ? 'Regenerate' : 'Generate'}
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  {requirements.generalInstructions && (
                    <div className="mt-3 p-3 bg-surface-sunk rounded-lg">
                      <p className="text-xs font-medium text-content mb-1">Formatting Requirements:</p>
                      <p className="text-xs text-content-muted">{requirements.generalInstructions}</p>
                    </div>
                  )}

                  {impliedSections.length > 0 && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <p className="text-xs font-medium text-blue-800 mb-1">Recommended Additional Sections:</p>
                      <ul className="space-y-0.5">
                        {impliedSections.map((s, i) => (
                          <li key={i} className="text-xs text-blue-700">- {s}</li>
                        ))}
                      </ul>
                      <p className="text-xs text-blue-500 mt-1.5 italic">Based on contract type and scope analysis</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-4">
                  <ExclamationTriangleIcon className="h-8 w-8 text-content-subtle mx-auto mb-2" />
                  <p className="text-sm text-content-muted">
                    {requirements === null
                      ? (requirementsMessage || 'Save solicitation documents to the AI Document Hub first, then come back here.')
                      : 'Could not determine section requirements from documents.'}
                  </p>
                </div>
              )}
            </div>

            {/* Generated sections list + export */}
            {sections.length > 0 && (
              <div className="bg-surface shadow rounded-lg p-5">
                <h3 className="font-medium text-content mb-3">Generated Sections</h3>
                <div className="space-y-2">
                  {sections.map((s, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveSection(idx)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        activeSection === idx ? 'bg-brand-50 text-brand-700 border border-brand-200' : 'hover:bg-surface-sunk text-content'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium truncate">{s.sectionType}</span>
                        <span className="text-xs text-content-subtle">{s.wordCount} words</span>
                      </div>
                    </button>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-border text-xs text-content-muted flex justify-between">
                  <span>{totalWords} total words</span>
                  <span>~{totalPages} pages</span>
                </div>
                {proposalId && (
                  <>
                    <button
                      onClick={runReview}
                      disabled={reviewing || sections.length === 0}
                      className="mt-3 w-full flex items-center justify-center gap-2 px-3 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 text-sm font-medium"
                    >
                      {reviewing ? 'Reviewing…' : 'Run Compliance Review'}
                    </button>
                    <button
                      onClick={() => { setShowMatrix(true); loadComplianceMatrix(); }}
                      className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 bg-surface border border-border-strong text-content rounded-lg hover:bg-surface-sunk text-sm font-medium"
                    >
                      View Compliance Matrix
                    </button>
                    <button
                      onClick={() => setShowPricing(true)}
                      className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium"
                    >
                      Manage Pricing
                    </button>
                    {/* Separate from "Manage Pricing", which is Clevor's own
                        cost model. This is the agency's spreadsheet, filled in
                        and handed back — a different deliverable. */}
                    <button
                      onClick={() => setShowBidSchedule(true)}
                      disabled={!proposalId}
                      className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 bg-surface border border-border-strong text-content rounded-lg hover:bg-surface-sunk text-sm font-medium disabled:opacity-50"
                    >
                      <TableCellsIcon className="h-4 w-4" />
                      Fill Bid Schedule
                    </button>
                    <button
                      onClick={() => setShowForms(true)}
                      className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 bg-surface border border-border-strong text-content rounded-lg hover:bg-surface-sunk text-sm font-medium"
                    >
                      Government Forms Pack
                    </button>
                    <button
                      onClick={() => setShowExportChecklist(true)}
                      disabled={exporting}
                      className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 text-sm font-medium"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      {exporting ? 'Exporting...' : 'Export as DOCX'}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Right — Generated content viewer */}
          <div className="lg:col-span-2">
            {activeSection !== null && sections[activeSection] ? (
              <div className="bg-surface shadow rounded-lg overflow-hidden">
                <div className="px-6 py-4 border-b border-border flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-content">{sections[activeSection].sectionType}</h3>
                    <p className="text-xs text-content-muted mt-0.5">{sections[activeSection].wordCount} words &middot; ~{sections[activeSection].estimatedPages} pages</p>
                  </div>
                  <button
                    onClick={() => copyToClipboard(sections[activeSection].content)}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-content-muted hover:text-content border border-border-strong rounded-lg hover:bg-surface-sunk"
                  >
                    <ClipboardDocumentIcon className="h-4 w-4" /> Copy
                  </button>
                </div>
                <div className="p-6">
                  {/* Raised where the text is read, not filed in a report.
                      The source material this was written from carries
                      placeholder markers and the generated prose does not keep
                      them, so a sentence that reads as an accomplished fact may
                      have come from data somebody typed as a stand-in. */}
                  {(sections[activeSection] as any)?.metadata?.placeholderSources && (
                    <div className="mb-4 rounded-lg border-2 border-red-400 bg-red-50 p-4">
                      <div className="flex gap-3">
                        <ExclamationTriangleIcon className="h-6 w-6 flex-shrink-0 text-red-600" aria-hidden="true" />
                        <div className="min-w-0">
                          <p className="text-base font-bold text-red-900">
                            Written from placeholder data
                          </p>
                          <p className="mt-1.5 text-sm text-red-800">
                            {(sections[activeSection] as any).metadata.placeholderSources.summary}
                          </p>
                          <ul className="mt-2 space-y-0.5 text-sm text-red-800">
                            {(sections[activeSection] as any).metadata.placeholderSources.sources.map(
                              (src: any, i: number) => (
                                <li key={i}>
                                  <span className="font-medium">{src.origin}</span>
                                  <span className="text-red-700"> — {src.markers.join(', ')}</span>
                                </li>
                              ),
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="prose prose-sm max-w-none text-content leading-relaxed">
                    {renderProposalText(sections[activeSection].content)}
                  </div>
                </div>
                {/* Compliance Score Badge */}
                {sections[activeSection].complianceScore != null && (
                  <div className="px-6 py-3 border-t border-border bg-surface-sunk">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-content">Compliance Score:</span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-semibold ${
                        sections[activeSection].complianceScore! >= 95 ? 'bg-green-100 text-green-800' :
                        sections[activeSection].complianceScore! >= 85 ? 'bg-blue-100 text-blue-800' :
                        sections[activeSection].complianceScore! >= 70 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {sections[activeSection].complianceScore}%
                      </span>
                      <span className="text-xs text-content-muted">
                        {sections[activeSection].complianceScore! >= 95 ? 'Submission-ready' :
                         sections[activeSection].complianceScore! >= 85 ? 'Strong draft — fill placeholders' :
                         sections[activeSection].complianceScore! >= 70 ? 'Needs review' :
                         'Significant gaps — review carefully'}
                      </span>
                    </div>
                  </div>
                )}

                {/* Compliance Gaps */}
                {sections[activeSection].complianceGaps && sections[activeSection].complianceGaps!.length > 0 && (
                  <div className="px-6 py-3 border-t border-border bg-yellow-50">
                    <h4 className="text-sm font-medium text-yellow-800 mb-2 flex items-center gap-1.5">
                      <ExclamationTriangleIcon className="h-4 w-4" />
                      Compliance Gaps
                    </h4>
                    <ul className="space-y-1">
                      {sections[activeSection].complianceGaps!.map((gap, i) => (
                        <li key={i} className="text-xs text-yellow-700 flex items-start gap-2">
                          <span className="text-yellow-500 mt-0.5">-</span>
                          {gap}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Compliance Recommendations */}
                {sections[activeSection].complianceRecommendations && sections[activeSection].complianceRecommendations!.length > 0 && (
                  <div className="px-6 py-3 border-t border-border bg-blue-50">
                    <h4 className="text-sm font-medium text-blue-800 mb-2">Recommendations</h4>
                    <ul className="space-y-1">
                      {sections[activeSection].complianceRecommendations!.map((rec, i) => (
                        <li key={i} className="text-xs text-blue-700 flex items-start gap-2">
                          <span className="text-blue-500 mt-0.5">-</span>
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Compliance Notes */}
                {sections[activeSection].complianceNotes.length > 0 && (
                  <div className="px-6 py-4 border-t border-border bg-surface-sunk">
                    <h4 className="text-sm font-medium text-content mb-2">Details</h4>
                    <ul className="space-y-1">
                      {sections[activeSection].complianceNotes.map((note, i) => (
                        <li key={i} className="text-xs text-content-muted flex items-start gap-2">
                          <CheckCircleIcon className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                          {note}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-surface shadow rounded-lg p-12 text-center">
                <DocumentTextIcon className="mx-auto h-16 w-16 text-content-subtle" />
                <h3 className="mt-4 text-title text-content">
                  {sections.length > 0 ? 'Select a section' : 'Generate your first section'}
                </h3>
                <p className="mt-2 text-content-muted max-w-md mx-auto">
                  {requirements?.sections
                    ? 'Click "Generate" on any required section to create solicitation-specific content.'
                    : 'Save solicitation documents from the opportunity detail page, then the system will analyze what sections are needed.'}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* No opportunity selected */}
      {!opportunityId && !loadingOpp && (
        <div className="bg-surface shadow rounded-lg p-12 text-center">
          <DocumentTextIcon className="mx-auto h-16 w-16 text-content-subtle" />
          <h3 className="mt-4 text-title text-content">Select an Opportunity</h3>
          <p className="mt-2 text-content-muted max-w-md mx-auto">
            Choose an opportunity from the dropdown above or start from an opportunity detail page using the "Build Proposal" button.
          </p>
        </div>
      )}

      {/* Export Checklist Modal */}
      <Modal
        open={showExportChecklist}
        onClose={() => { setShowExportChecklist(false); setSamAcknowledged(false); }}
        title="Export Proposal"
        size="md"
        footer={
          <>
            <button
              onClick={() => { setShowExportChecklist(false); setSamAcknowledged(false); }}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleExportDocx}
              disabled={exporting || (!!samNaicsCode && !samAcknowledged)}
              title={!!samNaicsCode && !samAcknowledged ? 'Confirm the NAICS registration above first' : undefined}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />
              {exporting ? 'Exporting…' : 'Export'}
            </button>
          </>
        }
      >
            <div>
              {/* Two treatments, because two different situations.
                  
                  Clevor cannot read a SAM registration — SAM is the system of
                  record and we have no access to it. So this is never a green
                  tick, and the code being present in a Clevor profile is not
                  proof of anything. But it is a signal, and the two cases
                  deserve different weight:
                  
                  Code absent from the profile — the user has told us, in their
                  own words, that they do not work under this code. The odds
                  their SAM registration carries it are low, and the consequence
                  is a non-responsive offer after the work is done. Red, and it
                  blocks the export until they say they have checked.
                  
                  Code present — probably fine, still not verifiable by us.
                  Worth a line, not worth a barrier. A confirmation that appears
                  on every export is one nobody reads by the third time, which
                  would cost exactly the case above. */}
              {/* One gate, two volumes.
                  
                  Clevor cannot read a SAM registration — SAM is the system of
                  record and we have no access to it — so this is never a green
                  tick, and the code sitting in a Clevor profile is evidence
                  rather than proof. That is why the acknowledgement is asked for
                  every time: it is the last gate before a federal submission and
                  the single failure that makes an otherwise sound quote
                  non-responsive.
                  
                  What changes is the volume, not the gate. A profile missing the
                  code means the user has told us in their own words that they do
                  not work under it, and that deserves to look alarming. A profile
                  carrying it probably means they are fine, and shouting about it
                  would train them to click past the case that matters. */}
              {samNaicsCode && (
                <div
                  className={`mb-4 rounded-lg p-4 ${
                    samNaicsGap
                      ? 'border-2 border-red-400 bg-red-50'
                      : 'border border-amber-300 bg-amber-50'
                  }`}
                >
                  <div className="flex gap-3">
                    <ExclamationTriangleIcon
                      className={`h-6 w-6 flex-shrink-0 ${samNaicsGap ? 'text-red-600' : 'text-amber-600'}`}
                      aria-hidden="true"
                    />
                    <div className="min-w-0">
                      {samNaicsGap ? (
                        <>
                          <p className="text-base font-bold text-red-900">
                            NAICS {samNaicsCode} is not one of your registered codes
                          </p>
                          <p className="mt-1.5 text-sm text-red-800">
                            This solicitation is evaluated under NAICS {samNaicsCode}, and your
                            small-business size status is measured against that code's size
                            standard. Your Clevor profile lists{' '}
                            <strong>{profileNaics.join(', ') || 'no codes'}</strong>.
                          </p>
                          <p className="mt-2 text-sm text-red-800">
                            If {samNaicsCode} is not on your SAM.gov entity registration, this
                            offer can be found <strong>non-responsive</strong> — the proposal is
                            not read, however good it is.
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="text-sm font-semibold text-amber-900">
                            Before you submit: confirm NAICS {samNaicsCode} is on your SAM
                            registration
                          </p>
                          <p className="mt-1 text-sm text-amber-800">
                            It's on your Clevor profile, which is a good sign but not the same
                            thing — SAM.gov is the system of record and Clevor can't read it.
                          </p>
                        </>
                      )}

                      <a
                        href="https://sam.gov/content/entity-registration"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`mt-2 inline-block text-sm font-semibold underline ${
                          samNaicsGap ? 'text-red-900' : 'text-amber-900'
                        }`}
                      >
                        Check your SAM registration →
                      </a>

                      <label
                        className={`mt-3 flex cursor-pointer items-start gap-2.5 rounded-md border bg-white p-2.5 ${
                          samNaicsGap ? 'border-red-300' : 'border-amber-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={samAcknowledged}
                          onChange={(e) => setSamAcknowledged(e.target.checked)}
                          className={`mt-0.5 h-4 w-4 flex-shrink-0 rounded ${
                            samNaicsGap
                              ? 'border-red-400 text-red-600 focus:ring-red-500'
                              : 'border-amber-400 text-amber-600 focus:ring-amber-500'
                          }`}
                        />
                        <span
                          className={`text-sm font-medium ${
                            samNaicsGap ? 'text-red-900' : 'text-amber-900'
                          }`}
                        >
                          I've confirmed NAICS {samNaicsCode} is on my SAM.gov registration, or
                          I will before I submit.
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Unfilled values, split by consequence.
                  
                  A generated proposal is meant to have blanks — the writer asks
                  rather than invents. But in the file they all look the same,
                  and "[INSERT OPTION YEAR 1 PRICE]" is not the same kind of gap
                  as "[INSERT NUMBER] years of experience". One means the
                  proposal is thinner than it could be; the other means the quote
                  is not evaluated at all. Listing them flat, or as a single
                  count, hides that. */}
              {gaps?.responsiveness?.length > 0 && (
                <div className="mb-4 rounded-lg border-2 border-amber-400 bg-amber-50 p-4">
                  <div className="flex gap-3">
                    <ExclamationTriangleIcon className="h-6 w-6 flex-shrink-0 text-amber-600" aria-hidden="true" />
                    <div className="min-w-0">
                      <p className="text-base font-bold text-amber-900">
                        Fill these before you submit
                      </p>
                      <p className="mt-1.5 text-sm text-amber-800">{gaps.summary}</p>
                      <ul className="mt-2 space-y-1 text-sm text-amber-900">
                        {gaps.responsiveness.map((g: any, i: number) => (
                          <li key={i} className="flex gap-2">
                            <span className="font-mono text-xs text-amber-700 whitespace-nowrap">
                              {g.occurrences}×
                            </span>
                            <span className="min-w-0">
                              <code className="text-xs">{g.placeholder}</code>
                              <span className="text-amber-700"> — {g.sectionTitle}</span>
                            </span>
                          </li>
                        ))}
                      </ul>
                      {gaps.routine?.length > 0 && (
                        <p className="mt-2.5 text-xs text-amber-700">
                          {gaps.routine.reduce((n: number, g: any) => n + g.occurrences, 0)} other
                          blanks are ordinary detail — worth filling, but they do not decide whether
                          the quote is read.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Repeated at the export gate. Someone who scrolled past it on
                  the section is about to send the file. */}
              {sectionsFromPlaceholders.length > 0 && (
                <div className="mb-4 rounded-lg border-2 border-red-400 bg-red-50 p-4">
                  <div className="flex gap-3">
                    <ExclamationTriangleIcon className="h-6 w-6 flex-shrink-0 text-red-600" aria-hidden="true" />
                    <div className="min-w-0">
                      <p className="text-base font-bold text-red-900">
                        {sectionsFromPlaceholders.length === 1 ? 'A section was' : `${sectionsFromPlaceholders.length} sections were`}{' '}
                        written from placeholder data
                      </p>
                      <p className="mt-1.5 text-sm text-red-800">
                        {sectionsFromPlaceholders.map((x) => x.title || x.sectionType).join(', ')} drew on
                        source material marked as test or placeholder. The generated text does not
                        keep those markers, so it now reads as fact. An unsupported experience or
                        past performance claim on a federal quote is a false-statement exposure.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {requirements?.sections ? (
                <div className="space-y-2 mb-4">
                  {requirements.sections.map((req: any, idx: number) => {
                    const status = getSectionStatus(req.name);
                    return (
                      <div key={idx} className={`flex items-center gap-3 p-2 rounded-lg ${status === 'generated' ? 'bg-green-50' : 'bg-amber-50'}`}>
                        {status === 'generated'
                          ? <CheckCircleIcon className="h-5 w-5 text-green-500" />
                          : <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />}
                        <div>
                          <p className="text-sm font-medium text-content">{req.name}</p>
                          {status === 'missing' && <p className="text-xs text-amber-700">Missing — generate before exporting</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}

              <div className="border-t border-border pt-4">
                <p className="text-sm text-content mb-2">Your proposal: <strong>{sections.length} sections</strong>, {totalWords} words, ~{totalPages} pages</p>

                {/* Radiogroup rather than loose buttons: arrow keys move
                    between formats and the selected one is announced. */}
                <div className="mb-3" role="radiogroup" aria-label="Export format">
                  <span className="label mb-1 block">Export Format</span>
                  <div className="flex gap-2">
                    {[
                      { value: 'docx-single', label: 'DOCX', desc: 'One Word document with every section' },
                      { value: 'docx', label: 'Volumes', desc: 'Submission package: one file per required volume (ZIP)' },
                      { value: 'pdf', label: 'PDF', desc: 'Portable Document Format' },
                      { value: 'xlsx', label: 'XLSX', desc: 'Excel spreadsheet' },
                    ].map(fmt => (
                      <button
                        key={fmt.value}
                        role="radio"
                        aria-checked={exportFormat === fmt.value}
                        title={fmt.desc}
                        onClick={() => setExportFormat(fmt.value)}
                        className={`flex-1 p-2 rounded-token border text-center text-body transition-colors ${
                          exportFormat === fmt.value
                            ? 'border-accent bg-accent-sunk text-accent font-semibold'
                            : 'border-border text-content-muted hover:bg-surface-sunk'
                        }`}
                      >
                        {fmt.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-caption text-content-muted mt-1">
                    {exportFormat === 'docx'
                      ? requirements?.volumes?.length > 1
                        ? `ZIP with ${requirements.volumes.length} volumes, named the way the solicitation asks`
                        : 'This solicitation asks for one volume, so this is a single Word document'
                      : exportFormat === 'xlsx'
                        ? 'Each section will be a separate worksheet'
                        : exportFormat === 'pdf'
                          ? 'Single PDF with all sections'
                          : 'Single Word document with all sections, in order'}
                  </p>
                </div>
              </div>
            </div>
      </Modal>

      {/* Bid Schedule Modal */}
      <Modal
        open={showBidSchedule}
        onClose={() => setShowBidSchedule(false)}
        title="Bid Schedule"
        size="xl"
        description="Price the agency's own schedule and download it completed. This sheet is usually a submission item — the price volume does not replace it."
      >
        {proposalId && <BidSchedulePanel proposalId={proposalId} />}
      </Modal>

      {/* Compliance Matrix Modal */}
      <Modal
        open={showMatrix}
        onClose={() => setShowMatrix(false)}
        title="Compliance Matrix"
        size="xl"
        description={
          matrixStats
            ? `${matrixStats.addressed} of ${matrixStats.total - matrixStats.na} requirements addressed (${matrixStats.coveragePct}% coverage)`
            : undefined
        }
      >
            {/* Wide table gets its own horizontal scroll container so the
                dialog never scrolls sideways as a whole. */}
            <div className="overflow-x-auto">
              {loadingMatrix ? (
                <SkeletonRows rows={6} cols={4} />
              ) : matrixItems.length === 0 ? (
                <p className="text-body text-content-muted">
                  No matrix items yet. Generate at least one section so the deep solicitation analysis runs.
                </p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b text-content-muted">
                      <th className="py-2 pr-3">Source</th>
                      <th className="py-2 pr-3">Requirement</th>
                      <th className="py-2 pr-3">Category</th>
                      <th className="py-2 pr-3">Section</th>
                      <th className="py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matrixItems.map((m: any) => (
                      <tr key={m.id} className="border-b align-top">
                        <td className="py-2 pr-3 font-mono text-xs text-content-muted whitespace-nowrap">
                          {m.sourceRef || '—'}
                        </td>
                        <td className="py-2 pr-3 max-w-xl">
                          <div className="text-content">{m.requirementText}</div>
                          {m.evidence && (
                            <div className="text-xs text-content-muted italic mt-1">{m.evidence.slice(0, 200)}…</div>
                          )}
                        </td>
                        <td className="py-2 pr-3 text-xs">
                          <span className="inline-block px-2 py-0.5 rounded bg-surface-sunk text-content">
                            {m.category.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="py-2 pr-3 text-xs text-content-muted">{m.targetSection || '—'}</td>
                        <td className="py-2 whitespace-nowrap">
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                              m.status === 'addressed'
                                ? 'bg-green-100 text-green-700'
                                : m.status === 'partial'
                                ? 'bg-amber-100 text-amber-700'
                                : m.status === 'n_a'
                                ? 'bg-surface-sunk text-content-muted'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {m.status === 'n_a' ? 'N/A' : m.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
      </Modal>

      {/* Review Result Modal */}
      <Modal
        open={showReview && !!reviewResult}
        onClose={() => setShowReview(false)}
        title="Compliance Review"
        size="xl"
        description={
          reviewResult
            ? `Average score: ${reviewResult.avgScore ?? 'N/A'}/100 · ${reviewResult.reviewedSections} sections reviewed`
            : undefined
        }
      >
            {/* Guarded because JSX children are built eagerly: <Modal open={false}>
                still constructs its children, so dereferencing reviewResult here
                threw on first render — before any effect ran — and the
                ErrorBoundary swallowed the whole page with "Cannot read
                properties of null (reading 'reviews')". Guarding `open` and
                `description` above is not enough. */}
            <div className="space-y-6">
              {(reviewResult?.reviews || []).map((r: any) => (
                <div key={r.sectionId} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-medium text-content">{r.sectionTitle}</h3>
                    {typeof r.overallScore === 'number' && (
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-2xl font-bold ${
                            r.overallScore >= 85
                              ? 'text-green-600'
                              : r.overallScore >= 65
                              ? 'text-amber-600'
                              : 'text-red-600'
                          }`}
                        >
                          {r.overallScore}
                        </span>
                        <span
                          className={`text-xs px-2 py-1 rounded-full font-medium ${
                            r.recommendation === 'submit_ready'
                              ? 'bg-green-100 text-green-700'
                              : r.recommendation === 'revise'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {(r.recommendation || '').replace('_', ' ')}
                        </span>
                      </div>
                    )}
                  </div>
                  {r.error && (
                    <div className="text-sm text-red-600">Error: {r.error}</div>
                  )}
                  {r.summary && <p className="text-sm text-content mb-3">{r.summary}</p>}
                  {r.missingRequirements?.length > 0 && (
                    <div className="mb-3">
                      <div className="text-sm font-medium text-red-700 mb-1">
                        Missing ({r.missingRequirements.length})
                      </div>
                      <ul className="text-sm text-content space-y-2">
                        {r.missingRequirements.map((g: any, i: number) => (
                          <li key={i} className="border-l-2 border-red-300 pl-3">
                            <div className="font-mono text-xs text-red-700">{g.sourceRef}</div>
                            <div>{g.requirement}</div>
                            <div className="text-xs text-content-muted italic mt-1">{g.guidance}</div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {r.partialRequirements?.length > 0 && (
                    <div className="mb-3">
                      <div className="text-sm font-medium text-amber-700 mb-1">
                        Partial ({r.partialRequirements.length})
                      </div>
                      <ul className="text-sm text-content space-y-2">
                        {r.partialRequirements.map((g: any, i: number) => (
                          <li key={i} className="border-l-2 border-amber-300 pl-3">
                            <div className="font-mono text-xs text-amber-700">{g.sourceRef}</div>
                            <div>{g.requirement}</div>
                            {g.currentState && <div className="text-xs text-content-muted">Current: {g.currentState}</div>}
                            <div className="text-xs text-content-muted italic mt-1">{g.guidance}</div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {r.formattingIssues?.length > 0 && (
                    <div className="mb-3">
                      <div className="text-sm font-medium text-content mb-1">
                        Formatting ({r.formattingIssues.length})
                      </div>
                      <ul className="text-sm text-content list-disc pl-5">
                        {r.formattingIssues.map((f: any, i: number) => (
                          <li key={i}>
                            <span className="font-medium">{f.issue}</span> — {f.fix}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {r.aiLanguageDetected?.length > 0 && (
                    <div className="mb-3">
                      <div className="text-sm font-medium text-content mb-1">
                        AI-language phrases ({r.aiLanguageDetected.length})
                      </div>
                      <ul className="text-sm text-content space-y-1">
                        {r.aiLanguageDetected.map((a: any, i: number) => (
                          <li key={i}>
                            <span className="line-through text-red-600">"{a.phrase}"</span>
                            <span className="text-content-muted ml-2">→ {a.suggestion}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {r.strengthsToKeep?.length > 0 && (
                    <div className="mb-1">
                      <div className="text-sm font-medium text-green-700 mb-1">Keep doing this</div>
                      <ul className="text-sm text-content list-disc pl-5">
                        {r.strengthsToKeep.map((s: string, i: number) => (
                          <li key={i}>{s}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
      </Modal>

      {/* Pricing Modal */}
      {showPricing && proposalId && (
        <PricingModal proposalId={proposalId} onClose={() => setShowPricing(false)} />
      )}

      {/* Government Forms Modal */}
      {showForms && (
        <GovernmentFormsModal
          opportunityId={opportunityId || undefined}
          onClose={() => setShowForms(false)}
        />
      )}
    </div>
  );
};

export default ProposalWriter;
